/**
 * OSNAP-INTERSECT-EMIT — pure-2D pairwise intersection geometry.
 *
 * AutoCAD's INTERSECTION (OSMODE bit 32) snaps to the geometric crossing
 * of two entities; APPARENT INTERSECTION ("appint", bit 2048) snaps where
 * the entities' INFINITE extensions (segment → infinite line, arc → full
 * circle) would cross. Neither can be produced by the per-entity
 * `appendCandidatesForEntity` (osnapCandidates.ts) — they need entity
 * PAIRS. This module supplies the analytic crossings; the resolver
 * (`resolveObjectSnap.ts`) iterates the aperture-bounded candidate pairs
 * and feeds them in.
 *
 * Coverage this slice = all STRAIGHT + CONIC pairs: LINE, polyline span (a
 * straight span is a segment, a BULGED span is the ARC the batcher draws —
 * @codex PR #785 round 3 F1 closed the bulge half of
 * OSNAP-INTERSECT-EMIT-S2), MESH-WIRE edge, CIRCLE, ARC. ELLIPSE × * and
 * SPLINE × * remain the documented follow-up (the still-open S2 arm —
 * genuinely harder analytic geometry). For the
 * common straight/conic cases the mode is fully FUNCTIONAL: a pair with no
 * computed crossing simply yields no candidate (exactly like hovering
 * where no intersection exists — NOT a silent always-noop).
 *
 * `bounded === true`  → INTERSECTION: the crossing must lie ON both
 *                       entities (within the segment span AND within the
 *                       arc's start→end sweep).
 * `bounded === false` → APPARENT INTERSECTION: ignore the on-span /
 *                       on-sweep test (segment → infinite line, arc → full
 *                       circle). For TRANSVERSAL crossings `appint ⊇
 *                       intersection` by construction; the bounded-only
 *                       degenerate joins (collinear-segment shared endpoint,
 *                       co-circular-arc shared endpoint) are INTERSECTION-only
 *                       — their extensions coincide (same infinite line / full
 *                       circle) so there is no isolated apparent crossing.
 *
 * No DOM / THREE imports — pure 2D math, fully unit-testable. Denominators
 * are guarded so parallel / degenerate / disjoint inputs return [] (never
 * NaN / Infinity).
 */

import {
  bulgeArc,
  distanceToSegment2D,
  isDefaultExtrusion,
} from "@dxf-ai-editor/drawing-model";

import { polylineCentrelineSamples } from "../adapters/geom/polylineWidthBand";
import type { RenderableEntity, Vec2, Vec3 } from "../types";
import { angleInArc, toVec2, type RawHit } from "./osnapCandidates";
import { osnapPolylineSweptRuns } from "./osnapSweptRuns";
import type { OsnapMode } from "./osnapTypes";

const EPS = 1e-9;

/** A straight segment in 2D — the `a→b` endpoints. */
export interface SegShape {
  kind: "seg";
  a: Vec2;
  b: Vec2;
}

/** A full circle (radius `r`, centre `c`). */
export interface CircleShape {
  kind: "circle";
  c: Vec2;
  r: number;
}

/** A circular arc — centre, radius, start/end angle, sweep direction. */
export interface ArcShape {
  kind: "arc";
  c: Vec2;
  r: number;
  start: number;
  end: number;
  ccw: boolean;
}

/** One of the intersectable primitive shapes (straight or conic). */
export type IntersectShape = SegShape | CircleShape | ArcShape;

/** True iff `t` is within [0,1] (segment span) up to EPS. */
function onSpan(t: number): boolean {
  return t >= -EPS && t <= 1 + EPS;
}

/** True iff two 2D points coincide within EPS. */
function samePoint(a: Vec2, b: Vec2): boolean {
  return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;
}

/**
 * Collinear-touch crossing for a degenerate (parallel/collinear) seg×seg
 * pair. AutoCAD's INTERSECTION at a collinear JOIN is the shared endpoint
 * — NOT the whole overlap range. Returns the single shared/coincident
 * endpoint ONLY when the two collinear segments meet END-TO-END at exactly
 * one point with DISJOINT interiors (the classic join, e.g.
 * (0,0)-(5,0) & (5,0)-(10,0) → (5,0)); `null` for:
 *   - genuinely parallel-but-offset segments (different supporting lines),
 *   - collinear segments that OVERLAP over a RANGE (one endpoint shared but
 *     interiors overlap, e.g. (0,0)-(10,0) & (5,0)-(10,0) coincide over
 *     [5,10]) — there is NO isolated crossing, only a coincident continuum.
 */
function collinearTouch(s1: SegShape, s2: SegShape): Vec2 | null {
  const r1x = s1.b.x - s1.a.x;
  const r1y = s1.b.y - s1.a.y;
  const len1 = Math.hypot(r1x, r1y);
  if (len1 < EPS) return null; // degenerate s1
  // s2.a must lie on the infinite line through s1 for the pair to be
  // collinear (not merely parallel-and-offset): the cross product of the
  // s1 direction with the s1.a→s2.a offset must be ~0.
  const offax = s2.a.x - s1.a.x;
  const offay = s2.a.y - s1.a.y;
  const crossA = (r1x * offay - r1y * offax) / len1; // perpendicular distance
  if (Math.abs(crossA) > EPS) return null; // parallel but offset → no touch
  // s2.b must ALSO lie on the same supporting line (a true seg, not a point):
  // if only one endpoint were on the line the pair could not be collinear.
  const offbx = s2.b.x - s1.a.x;
  const offby = s2.b.y - s1.a.y;
  const crossB = (r1x * offby - r1y * offbx) / len1;
  if (Math.abs(crossB) > EPS) return null;
  // Both segments share one supporting line. Reduce to the 1-D overlap test
  // on the scalar parameter `t = (p - s1.a)·d / |d|` (signed distance along
  // the s1 direction). s1 spans [0, len1]; project s2's endpoints too.
  const dot = (px: number, py: number): number =>
    ((px - s1.a.x) * r1x + (py - s1.a.y) * r1y) / len1;
  const a1 = 0;
  const b1 = len1;
  const t2a = dot(s2.a.x, s2.a.y);
  const t2b = dot(s2.b.x, s2.b.y);
  const lo1 = Math.min(a1, b1);
  const hi1 = Math.max(a1, b1);
  const lo2 = Math.min(t2a, t2b);
  const hi2 = Math.max(t2a, t2b);
  // 1-D interval overlap of [lo1,hi1] and [lo2,hi2].
  const overlapLo = Math.max(lo1, lo2);
  const overlapHi = Math.min(hi1, hi2);
  if (overlapHi < overlapLo - EPS) return null; // disjoint along the line → no touch
  if (overlapHi > overlapLo + EPS) return null; // overlap over a RANGE → no isolated INT point
  // overlapHi ≈ overlapLo → the intervals meet at a SINGLE point (end-to-end
  // join, disjoint interiors). Map that scalar back to a world point on s1.
  const tTouch = overlapLo; // == overlapHi within EPS
  return { x: s1.a.x + (r1x / len1) * tTouch, y: s1.a.y + (r1y / len1) * tTouch };
}

/** True iff the point at world angle around `c` lies within the arc sweep. */
function pointOnArc(p: Vec2, arc: ArcShape): boolean {
  const ang = Math.atan2(p.y - arc.c.y, p.x - arc.c.x);
  return angleInArc(ang, arc.start, arc.end, arc.ccw);
}

/** World position of the arc point at world angle `ang` (radius `r`). */
function arcPointAtAngle(arc: ArcShape, ang: number): Vec2 {
  return {
    x: arc.c.x + arc.r * Math.cos(ang),
    y: arc.c.y + arc.r * Math.sin(ang),
  };
}

/** Sweep magnitude (radians, ≥0) of an arc from start→end in its direction. */
function arcSweepMagnitude(arc: ArcShape): number {
  const TWO_PI = Math.PI * 2;
  // Raw signed delta in the arc's travel direction.
  let delta = arc.end - arc.start;
  if (arc.ccw) {
    // CCW: end is reached by increasing angle.
    delta = ((delta % TWO_PI) + TWO_PI) % TWO_PI;
  } else {
    // CW: end is reached by decreasing angle; magnitude is the positive run.
    delta = ((-delta % TWO_PI) + TWO_PI) % TWO_PI;
  }
  return delta;
}

/**
 * Angular length (radians, ≥0) over which two CO-CIRCULAR arcs `a1` and `a2`
 * share their sweeps. Used to decide whether the two arcs coincide over a
 * RANGE (overlap > EPS → a continuum, NOT an isolated INT point) versus meet
 * at only a single endpoint angle (overlap ≈ 0 → a genuine end-to-end join).
 *
 * Both arcs lie on the SAME circle; we measure each arc as an interval on a
 * 1-D angular axis anchored at `a1.start` in a1's travel direction: a1 spans
 * [0, sweep1]; a2 maps to [lo2, lo2+sweep2] (using a2's endpoint offsets into
 * a1's frame, accounting for opposed travel direction). The returned value is
 * the size of the intersection of those two intervals on the circular axis
 * (checking the ±2π wrap copies of a2's interval too).
 */
function coCircularSweepOverlap(a1: ArcShape, a2: ArcShape): number {
  const TWO_PI = Math.PI * 2;
  const sweep1 = arcSweepMagnitude(a1);
  const sweep2 = arcSweepMagnitude(a2);
  // Travel direction of a1 as a sign on the angle.
  const dir1 = a1.ccw ? 1 : -1;
  // Offset (in a1's direction, normalised [0,2π)) of an absolute angle θ
  // from a1.start.
  const off1 = (theta: number): number => {
    const o = dir1 * (theta - a1.start);
    return ((o % TWO_PI) + TWO_PI) % TWO_PI;
  };
  // a2's run, expressed as the set of offsets-into-a1 it covers. a2 sweeps
  // from a2.start by sweep2 in a2's own direction; sample its endpoints'
  // offsets-into-a1. Because both are on the same circle, a2 maps to a single
  // contiguous offset interval [o2lo, o2lo+sweep2] (mod 2π) when its travel
  // aligns with a1, or the reverse run when opposed — we test the actual
  // covered offsets by walking a2 in a1's frame.
  const dir2 = a2.ccw ? 1 : -1;
  // a2 start/end offsets into a1's frame.
  const o2start = off1(a2.start);
  const o2end = off1(a2.end);
  // Direction of a2 within a1's offset axis: aligned (+1) if both same
  // rotational sense, opposed (−1) otherwise.
  const sameSense = dir1 === dir2;
  // The offset interval a2 covers, as [lo, hi] within [0, 2π) — possibly
  // wrapping. When aligned, a2 runs o2start → o2start+sweep2 increasing.
  // When opposed, a2 runs o2start → o2start−sweep2 (i.e. o2end → o2start
  // increasing). Normalise to an increasing [lo, hi].
  let lo2: number;
  if (sameSense) {
    lo2 = o2start;
  } else {
    lo2 = o2end;
  }
  // Overlap of a1's [0, sweep1] with a2's [lo2, lo2+sweep2] on the circular
  // axis. Compute the linear overlap of [0,sweep1] with the (possibly
  // wrapped) interval by splitting the wrap.
  const intervalOverlap = (
    aLo: number,
    aHi: number,
    bLo: number,
    bHi: number,
  ): number => Math.max(0, Math.min(aHi, bHi) - Math.max(aLo, bLo));
  const hi2 = lo2 + sweep2;
  let overlap = intervalOverlap(0, sweep1, lo2, hi2);
  // Account for the circular wrap: a2's interval, shifted by ±2π, may also
  // overlap a1's [0, sweep1].
  overlap = Math.max(overlap, intervalOverlap(0, sweep1, lo2 - TWO_PI, hi2 - TWO_PI));
  overlap = Math.max(overlap, intervalOverlap(0, sweep1, lo2 + TWO_PI, hi2 + TWO_PI));
  return overlap;
}

/**
 * Shared-endpoint crossings for two CO-CIRCULAR arcs (same centre + radius
 * within EPS). The radical-line method degenerates for coincident circles
 * (zero centre distance), so concentric/coincident pairs are early-returned
 * by `conicConic` BEFORE the sweep test — but two arcs lying on the SAME
 * circle that meet END-TO-END at a SINGLE endpoint angle (e.g. 0→π/2 and
 * π/2→π on r5 touch at (0,5)) DO have a real discrete INTERSECTION there.
 *
 * Arcs that OVERLAP over a sweep RANGE (e.g. 0→π and π/2→π share the (−5,0)
 * end but coincide over [π/2,π]) have NO isolated crossing — the coincident
 * sub-arc is a continuum, not a point — so this returns []. We first measure
 * the angular overlap: if the two sweeps share more than an EPS-sized angular
 * interval, bail (overlap). Otherwise enumerate the four endpoint pairs and
 * return those that coincide. Non-touching co-circular arcs return [].
 * (APPINT extends each arc to the full circle, which then coincides → no
 * isolated apparent crossing, so this is bounded-only.)
 */
function coCircularArcTouch(a1: ArcShape, a2: ArcShape): Vec2[] {
  // Overlap over a RANGE → no isolated INT point (coincident continuum).
  if (coCircularSweepOverlap(a1, a2) > EPS) return [];
  const ends1 = [arcPointAtAngle(a1, a1.start), arcPointAtAngle(a1, a1.end)];
  const ends2 = [arcPointAtAngle(a2, a2.start), arcPointAtAngle(a2, a2.end)];
  const out: Vec2[] = [];
  for (const e1 of ends1) {
    for (const e2 of ends2) {
      if (samePoint(e1, e2) && !out.some((p) => samePoint(p, e1))) {
        out.push(e1);
      }
    }
  }
  return out;
}

/**
 * Segment × segment crossing. `bounded` → both parameters must lie on
 * their span; otherwise the infinite-line crossing. Parallel / collinear
 * inputs (zero denominator) return [].
 */
function segSeg(s1: SegShape, s2: SegShape, bounded: boolean): Vec2[] {
  const r1x = s1.b.x - s1.a.x;
  const r1y = s1.b.y - s1.a.y;
  const r2x = s2.b.x - s2.a.x;
  const r2y = s2.b.y - s2.a.y;
  const denom = r1x * r2y - r1y * r2x;
  if (Math.abs(denom) < EPS) {
    // Parallel / collinear / degenerate: no single transversal crossing.
    // For bounded INTERSECTION, a COLLINEAR pair that touches at a shared
    // endpoint (a collinear join, e.g. (0,0)-(5,0) & (5,0)-(10,0)) is a
    // real discrete INT point — AutoCAD snaps INT to that shared endpoint.
    // For appint, two collinear infinite lines coincide (no isolated
    // apparent intersection) and parallel-offset lines never cross.
    if (bounded) {
      const touch = collinearTouch(s1, s2);
      if (touch) return [touch];
    }
    return [];
  }
  const dx = s2.a.x - s1.a.x;
  const dy = s2.a.y - s1.a.y;
  const t = (dx * r2y - dy * r2x) / denom;
  const u = (dx * r1y - dy * r1x) / denom;
  if (bounded && (!onSpan(t) || !onSpan(u))) return [];
  return [{ x: s1.a.x + r1x * t, y: s1.a.y + r1y * t }];
}

/**
 * Infinite-line (through segment `s`) × circle/arc crossings. When
 * `bounded`, each crossing must lie on the segment span AND (for an arc)
 * within the arc sweep. Tangent → one point; miss → [].
 */
function segConic(
  s: SegShape,
  conic: CircleShape | ArcShape,
  bounded: boolean,
): Vec2[] {
  const dx = s.b.x - s.a.x;
  const dy = s.b.y - s.a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < EPS) return []; // degenerate segment
  // Param along the infinite line: |s.a + t·d − c|² = r².
  const fx = s.a.x - conic.c.x;
  const fy = s.a.y - conic.c.y;
  const aQ = len2;
  const bQ = 2 * (fx * dx + fy * dy);
  const cQ = fx * fx + fy * fy - conic.r * conic.r;
  const disc = bQ * bQ - 4 * aQ * cQ;
  if (disc < -EPS) return []; // no real crossing
  const sqrtD = Math.sqrt(Math.max(0, disc));
  const ts =
    sqrtD < EPS ? [-bQ / (2 * aQ)] : [(-bQ - sqrtD) / (2 * aQ), (-bQ + sqrtD) / (2 * aQ)];
  const out: Vec2[] = [];
  for (const t of ts) {
    if (bounded && !onSpan(t)) continue;
    const p = { x: s.a.x + dx * t, y: s.a.y + dy * t };
    if (bounded && conic.kind === "arc" && !pointOnArc(p, conic)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Circle/arc × circle/arc crossings (the radical-line method). When
 * `bounded`, each crossing must lie within BOTH arcs' sweeps. Concentric
 * / disjoint / containing circles return []; tangent → one point.
 */
function conicConic(
  c1: CircleShape | ArcShape,
  c2: CircleShape | ArcShape,
  bounded: boolean,
): Vec2[] {
  const dx = c2.c.x - c1.c.x;
  const dy = c2.c.y - c1.c.y;
  const d2 = dx * dx + dy * dy;
  const d = Math.sqrt(d2);
  if (d < EPS) {
    // Concentric (incl. coincident centre). The radical-line method has no
    // discrete crossing here — BUT two ARCS on the SAME circle (equal radius)
    // that touch at a shared endpoint are a real bounded INTERSECTION at that
    // endpoint (e.g. 0→π/2 and π/2→π on r5 touch at (0,5)). Non-touching
    // co-circular arcs, full-circle pairs, and unequal-radius concentric
    // pairs all yield no isolated crossing.
    if (
      bounded &&
      c1.kind === "arc" &&
      c2.kind === "arc" &&
      Math.abs(c1.r - c2.r) < EPS
    ) {
      return coCircularArcTouch(c1, c2);
    }
    return [];
  }
  const r1 = c1.r;
  const r2 = c2.r;
  if (d > r1 + r2 + EPS) return []; // disjoint
  if (d < Math.abs(r1 - r2) - EPS) return []; // one inside the other
  // Distance from c1 to the radical line foot, then the half-chord height.
  const a = (d2 + r1 * r1 - r2 * r2) / (2 * d);
  const h2 = r1 * r1 - a * a;
  const h = Math.sqrt(Math.max(0, h2));
  const px = c1.c.x + (a * dx) / d;
  const py = c1.c.y + (a * dy) / d;
  const ux = -dy / d;
  const uy = dx / d;
  const pts =
    h < EPS
      ? [{ x: px, y: py }] // tangent — single point
      : [
          { x: px + ux * h, y: py + uy * h },
          { x: px - ux * h, y: py - uy * h },
        ];
  if (!bounded) return pts;
  return pts.filter(
    (p) =>
      (c1.kind === "circle" || pointOnArc(p, c1)) &&
      (c2.kind === "circle" || pointOnArc(p, c2)),
  );
}

/**
 * Crossings of two primitive shapes. `bounded === true` → INTERSECTION
 * (on-span / on-sweep enforced); `bounded === false` → APPARENT
 * INTERSECTION (each shape extended: segment → infinite line, arc → full
 * circle). Order-independent; returns 0, 1 (tangent) or 2 points. Never
 * returns NaN / Infinity — every denominator is EPS-guarded.
 */
export function intersectShapes(
  s1: IntersectShape,
  s2: IntersectShape,
  bounded: boolean,
): Vec2[] {
  if (s1.kind === "seg") {
    return s2.kind === "seg"
      ? segSeg(s1, s2, bounded)
      : segConic(s1, s2, bounded);
  }
  // s1 is a conic from here.
  if (s2.kind === "seg") return segConic(s2, s1, bounded);
  return conicConic(s1, s2, bounded);
}

/**
 * Decompose a renderable entity into its intersectable straight / conic
 * shapes. LINE → one segment; LWPOLYLINE / heavy POLYLINE → one shape per span
 * (closed polylines include the wrap-around span): a straight span yields a
 * `seg`, and a BULGED span yields the ARC it actually is. The chord used to be
 * emitted instead, because the live mesh renderer drew bulged spans flat and
 * snapping the chord kept snap ≡ render (@codex round-3 #457); the batcher now
 * TESSELLATES those spans (`polylineWidthBand.ts::polylineCentrelineSamples`), so
 * the arc
 * is what is on screen and the arc is what must be crossed — the two halves of
 * OSNAP-INTERSECT-EMIT-S2 (docs/test-gaps/sub-osnap.md) landing together, as
 * that item requires (@codex PR #785 round 3, F1). MESH-WIRE → one `seg` per
 * drawn mesh edge (a mesh's runs are bulge-free by construction). CIRCLE → one
 * circle; ARC → one arc. ELLIPSE / SPLINE / compound kinds yield [] this slice
 * (deferred to OSNAP-INTERSECT-EMIT-S2 — see module docstring), so a line ×
 * ellipse with no computed crossing simply produces no candidate.
 */
/**
 * ONE run — a polyline outline, a swept top outline, a group-39 RULING or a
 * mesh wire — as its intersectable shapes, one per span (a CLOSED run includes
 * the wrap-around span).
 *
 * `bulges` present ⇒ a non-zero group-42 entry makes that span the ARC the
 * batcher draws, through `drawing-model`'s one-truth {@link bulgeArc} (@codex
 * PR #785 round 3, F1 — emitting the straight chord instead would put the
 * INTERSECTION snap OFF the drawn geometry: the (0,5) apex of a (−5,0)→(5,0)
 * bulge=1 semicircle would resolve at y=0). `bulges` ABSENT ⇒ every span is a
 * straight `seg`, which is what a mesh wire, a RULING and an already-tessellated
 * tilted chain each are — one walk for all four callers rather than four copies
 * of the same index arithmetic.
 */
function* runShapes(
  pts: readonly Vec3[],
  closed: boolean,
  bulges: number[] | undefined,
): Generator<IntersectShape> {
  const n = pts.length;
  if (n < 2) return;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    if (!a || !b) continue;
    const av = toVec2(a);
    const bv = toVec2(b);
    const arc = bulges === undefined ? null : bulgeArc(av, bv, bulges[i] ?? 0);
    if (arc) {
      yield {
        kind: "arc",
        c: { x: arc.cx, y: arc.cy },
        r: arc.radius,
        start: arc.startAngle,
        end: arc.startAngle + arc.signedSweep,
        ccw: arc.signedSweep > 0,
      };
      continue;
    }
    yield { kind: "seg", a: av, b: bv };
  }
}

export function* streamShapesForEntity(
  entity: RenderableEntity,
): Generator<IntersectShape> {
  const g = entity.geometry;
  switch (g.kind) {
    case "line":
      yield { kind: "seg", a: toVec2(g.a), b: toVec2(g.b) };
      return;
    case "mesh-wire": {
      // A mesh's wire runs are bulge-free by construction, so every span is a
      // `seg` — the SAME walk a polyline run takes, asked for with no bulges
      // rather than written out a second time.
      for (const run of g.runs) {
        yield* runShapes(run.points, run.closed, undefined);
      }
      return;
    }
    case "polyline": {
      // A TILTED OCS-planar entity's bulged span is a circular arc in ITS OWN
      // plane, which PROJECTS to an elliptical arc — a shape this module's
      // `seg` / `arc` / `circle` vocabulary cannot state. What the batcher
      // actually draws for it is the tessellated chain
      // (`adapters/geom/polylineWidthBand.ts::polylineCentrelineSamples`), so
      // the crossing is computed against exactly that chain: still snap ≡
      // render, and no second subdivision of the same arc. The common
      // default-extrusion polyline is untouched below.
      const pts = isDefaultExtrusion(g.extrusion)
        ? g.points
        : polylineCentrelineSamples(
            g.points,
            g.closed,
            g.bulges,
            g.extrusion,
          );
      const tessellated = pts !== g.points;
      if (pts.length < 2) return;
      // THE GROUP-39 THICKNESS SWEEP (@codex PR #785 round 16, the OSNAP
      // fan-out). Round 15 gave the canvas the swept surface and this arm went
      // on crossing the unswept base, so a LINE drawn across the PAINTED top
      // outline of an obliquely-extruded polyline produced no INTERSECTION
      // candidate at all — the same visible-but-unreachable regression
      // `raycast/distance.ts` closed for the pointer pick. `osnapSweptRuns.ts`
      // is the one projection of that sweep onto the snap surface (and carries
      // the AutoCAD argument for sweeping the CENTRELINE); a thickness-less
      // polyline yields exactly one run and the walk below is byte-identical
      // to the one it replaced.
      for (const run of osnapPolylineSweptRuns(g, pts)) {
        // A RULING is straight whatever the base declares, and a TESSELLATED
        // base has already had its bows subdivided into chords — either way the
        // walk is asked for WITHOUT bulges and yields plain segments.
        yield* runShapes(
          run.points,
          run.closed,
          run.ruling || tessellated ? undefined : g.bulges,
        );
      }
      return;
    }
    case "circle":
      yield { kind: "circle", c: toVec2(g.center), r: g.radius };
      return;
    case "arc":
      yield {
        kind: "arc",
        c: toVec2(g.center),
        r: g.radius,
        start: g.start,
        end: g.end,
        ccw: g.ccw,
      };
      return;
    default:
      // ellipse / spline / point / block-insert / text / mtext / compound
      // kinds — no straight-or-conic intersection shape this slice.
      return;
  }
}

/**
 * Decompose a renderable entity into its intersectable straight / conic shapes
 * as a materialized array. Thin wrapper over the `streamShapesForEntity`
 * generator (the single decomposition one-truth) for callers that genuinely
 * need the full array — the per-resolve hot path STREAMS the generator instead
 * (so a 100k-segment LWPOLYLINE under the cursor never allocates 100k objects;
 * see `collectBoundedNearCursorShapes` / `collectAppintShapesRoundRobin`).
 *
 * LINE → one segment; LWPOLYLINE / heavy POLYLINE → one shape per span (closed
 * polylines include the wrap-around span): a straight span is a `seg`, a BULGED
 * span is the ARC the batcher draws (@codex PR #785 round 3, F1). MESH-WIRE →
 * one `seg` per drawn mesh edge. CIRCLE → one circle; ARC → one arc. ELLIPSE /
 * SPLINE / compound kinds yield [] this slice (deferred to
 * OSNAP-INTERSECT-EMIT-S2), so a line × ellipse with no computed crossing
 * simply produces no candidate.
 */
export function shapesForEntity(entity: RenderableEntity): IntersectShape[] {
  return [...streamShapesForEntity(entity)];
}

/**
 * True iff `entity`'s geometry kind can yield AT LEAST ONE straight/conic
 * intersection shape (i.e. `streamShapesForEntity` would emit something for it):
 * LINE / LWPOLYLINE / CIRCLE / ARC contribute; everything else (point / text /
 * mtext / hatch / ellipse / spline / block-insert / compound …) yields nothing
 * THIS slice. Kept in lock-step with the `streamShapesForEntity` switch (the
 * single decomposition one-truth) — the cases below MUST match the contributing
 * cases there.
 *
 * Used by the APPINT entity-gather (`gatherAppintEntities` in
 * `resolveObjectSnap.ts`, @codex round-6 #3) so a resolvable-but-non-
 * intersectable broad id does NOT consume one of the scarce
 * `INTERSECT_PASS_ENTITY_CAP` slots — otherwise a broadened window dominated by
 * points/text/hatch fills the cap with entities that produce no crossing and the
 * real far-stub crossings are starved. A LWPOLYLINE with < 2 points yields no
 * segment, so it is treated as non-contributing too (matches the `n < 2`
 * early-return in `streamShapesForEntity`).
 */
export function entityCanProduceIntersectShape(
  entity: RenderableEntity,
): boolean {
  const g = entity.geometry;
  switch (g.kind) {
    case "line":
    case "circle":
    case "arc":
      return true;
    case "polyline":
      // A polyline contributes only when it has at least one edge (≥ 2 points) —
      // mirrors the `n < 2` early-return in `streamShapesForEntity`.
      return g.points.length >= 2;
    default:
      return false;
  }
}

/**
 * Defensive cap on the per-resolve pairwise pass. The candidate set is
 * already aperture-bounded (small N — only entities whose AABB touches the
 * cursor aperture box), so the O(N²) shape-pair sweep is cheap in
 * practice. If a pathological aperture surfaces a huge candidate list we
 * cap the ENTITY count (NOT silently — a rate-limited console.warn fires)
 * so the snap pass can never pin the frame.
 */
export const INTERSECT_PASS_ENTITY_CAP = 200;

/**
 * Total-PRIMITIVE-SHAPE cap across ALL aperture entities. The entity cap
 * alone is insufficient: a single large LWPOLYLINE contributes one shape
 * PER edge, so two 10k-segment polylines (2 entities, under the entity cap)
 * still produce ~100M `intersectShapes` calls per pointer move. Capping the
 * total shapes gathered bounds the O(M²) shape-pair sweep regardless of how
 * the edges are distributed across entities. 4000 shapes → ≤~8M pairs,
 * comfortably under one frame. Truncation is NOT silent — it shares the
 * rate-limited warn below.
 */
export const INTERSECT_PASS_SHAPE_CAP = 4000;

/**
 * Total-shapes-SCANNED backstop for the BOUNDED-INTERSECTION streaming gather
 * (@codex round-4 #651-followup). The shape cap above bounds shapes RETAINED,
 * but a streaming filter that COLLECTS nothing never trips it: when many large
 * AABB-but-FAR entities overlap the cursor aperture (so the spatial index
 * returns them) yet none of their segments are near-cursor, the collector
 * streams EVERY segment of EVERY entity, retains zero, and the retained-cap can
 * never fire. That re-introduces the exact pathological-density frame-pin the
 * caps exist to prevent (e.g. 200 polylines × 5000 far segments = 1M segments
 * scanned per hover). This backstop counts every segment/arc EXAMINED (not just
 * retained) and breaks once the SCAN budget is exhausted, so the bound caps WORK
 * — never just retained shapes.
 *
 * Set generously high — above a single LEGITIMATE very-large polyline's edge
 * count (a 100k-segment LWPOLYLINE under the cursor must still be fully scanned
 * so its near-cursor edges, wherever along the span they sit, are reached) while
 * still bounding the genuinely pathological MANY-entity million-segment case to a
 * few-millisecond examination budget. Each examination is ~4 AABB comparisons.
 * It shares the rate-limited over-cap warn. 200k examined is a couple ms (well
 * under a frame); the multi-entity 1M-segment case is bounded to 200k (~5x
 * faster than the un-capped round-3 behaviour) and warns once.
 */
export const INTERSECT_PASS_SCAN_CAP = 200_000;

/**
 * Defensive cap on the number of intersection RawHits EMITTED per pairwise pass
 * (@codex round-5 #1). The gather caps the shapes COLLECTED, but the O(M²)
 * pairwise sweep can still produce an unbounded number of CROSSINGS: a dense
 * aperture packed with many short entities/segments all crossing near one point
 * appends a RawHit for EVERY crossing, so `out` grows without bound per hover
 * even though the shape count is capped.
 *
 * CORRECTNESS: the resolver later picks the BEST candidate under its documented
 * tie-break (minimum `distWorld`, THEN lexically-smaller `ownerEntityId`), so a
 * naive append-then-stop could DROP the candidate the resolver would pick if it
 * is found after the cap fills. Instead the pass keeps a bounded BEST-K buffer of
 * this size ordered by the SAME tie-break — when the buffer is full and a new
 * crossing is BETTER than the current worst retained (strictly closer, OR equal
 * distance with a smaller ownerEntityId), the worst is evicted — so the candidate
 * the uncapped resolver would pick is NEVER lost to the emit cap, even at a
 * distance tie broken by ownerEntityId (@codex round-6 #4). K is set generously
 * above what any single resolve needs (the resolver consumes only the single
 * closest) so it bites only in pathological density; truncation shares the
 * rate-limited over-cap warn.
 */
export const INTERSECT_PASS_EMIT_CAP = 256;

/**
 * DYNAMIC per-OWNER near-cursor fair-share for the BOUNDED-INTERSECTION gather
 * (@codex round-4-followup multi-owner fairness, REVISED round-5 #742).
 *
 * The bounded gather fills the total-shape budget ROUND-ROBIN across owners (one
 * near-cursor shape per owner per round), which by itself already distributes the
 * budget fairly — a sparse crossing line always gets its turn before any single
 * owner can exhaust the budget. To additionally bound a single dense owner when
 * MANY owners contend for the budget, each owner is held to a FAIR SHARE of the
 * GLOBAL `INTERSECT_PASS_SHAPE_CAP`: `ceil(SHAPE_CAP / ownerCount)`.
 *
 * CRITICAL (round-5 #742): the fair share is computed from the GLOBAL budget, so
 * it ONLY bites under actual global pressure (many owners). With ONE owner the
 * share is the whole `SHAPE_CAP`, so a single polyline with hundreds of
 * near-cursor segments under the cursor (global budget free) collects them ALL —
 * the prior FIXED 512 ceiling wrongly retired such an owner at its 513th
 * near-cursor segment while the global cap still had thousands of free slots, so
 * a line crossing that owner's 513th+ near-cursor segment was MISSED with no cap
 * warning. The fair-share never drops near-cursor shapes while global budget
 * remains; when it DOES truncate an owner (only possible with multiple contending
 * owners), the gather marks `shapeCapped` so `warnIntersectCapOnce` fires.
 *
 * The denominator is the count of CONTRIBUTING owners (those whose kind yields
 * ≥1 straight/conic shape — @codex round-6 #2), NOT the raw candidate count: a
 * dense aperture of non-contributing owners (point/text/block-insert/ellipse/
 * spline/compound, which stream ZERO shapes) must not shrink the fair share and
 * starve a legit dense polyline while the global budget is mostly free. It is
 * clamped to ≥1 (an empty / all-non-contributing set never divides by zero);
 * with one contributing owner the share equals `SHAPE_CAP`.
 */
function intersectPassPerOwnerFairShare(ownerCount: number): number {
  return Math.ceil(INTERSECT_PASS_SHAPE_CAP / Math.max(1, ownerCount));
}

/**
 * Module-level once-per-session guard for the over-cap warning. The pass
 * runs on EVERY pointer move (and TWICE when INT + APPINT are both active),
 * so a per-call `console.warn` floods the console and adds per-frame cost.
 * This flag makes the warning genuinely fire once per session — covering
 * BOTH the entity cap and the shape-pair cap — then stays quiet.
 */
let intersectCapWarned = false;

/**
 * Emit the over-cap warning at most once per session (rate-limited). Exported so
 * the resolver's APPINT entity pre-cap (`gatherAppintEntities` in
 * `resolveObjectSnap.ts`) shares the SAME once-per-session guard (@codex round-5
 * #2): that pre-cap truncates the broadened+narrow set to
 * `INTERSECT_PASS_ENTITY_CAP` BEFORE this module runs, and its drop must surface
 * through the same rate-limited channel rather than being silent.
 */
export function warnIntersectCapOnce(message: string): void {
  if (intersectCapWarned) return;
  intersectCapWarned = true;
  console.warn(message);
}

/** Test-only reset of the once-per-session warn guard. */
export function __resetIntersectCapWarnForTest(): void {
  intersectCapWarned = false;
}

/** A primitive shape tagged with the entity it came from (for tie-break). */
export interface OwnedShape {
  shape: IntersectShape;
  ownerId: string;
}

/**
 * AABB of a primitive shape. A SEG → its endpoint hull; a CIRCLE → the full
 * circle box; an ARC → the FULL-circle box (a conservative superset of the
 * arc's true extent — never under-reports, so the aperture prefilter can never
 * drop a shape that could produce an in-aperture crossing).
 */
function shapeBounds(s: IntersectShape): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (s.kind === "seg") {
    return {
      minX: Math.min(s.a.x, s.b.x),
      minY: Math.min(s.a.y, s.b.y),
      maxX: Math.max(s.a.x, s.b.x),
      maxY: Math.max(s.a.y, s.b.y),
    };
  }
  // circle | arc — conservative full-circle box.
  return {
    minX: s.c.x - s.r,
    minY: s.c.y - s.r,
    maxX: s.c.x + s.r,
    maxY: s.c.y + s.r,
  };
}

/**
 * True iff the shape's AABB intersects the cursor-aperture box
 * `[cx±aperture]×[cy±aperture]`. A BOUNDED (INTERSECTION) crossing lies ON the
 * shape, so its finite AABB straddles any in-aperture crossing — a shape whose
 * AABB misses the aperture box can produce NO in-aperture bounded crossing and
 * is safely pre-filtered out. (NOT used for APPINT: an apparent crossing lies on
 * the INFINITE extension, which can be arbitrarily far from the finite AABB, so
 * the prefilter would wrongly drop far stubs whose extensions cross under the
 * cursor — APPINT relies on the entity-broaden + cap path instead.)
 */
function shapeWithinAperture(
  s: IntersectShape,
  cursor: Vec2,
  apertureWorld: number,
): boolean {
  const b = shapeBounds(s);
  return (
    b.minX <= cursor.x + apertureWorld &&
    b.maxX >= cursor.x - apertureWorld &&
    b.minY <= cursor.y + apertureWorld &&
    b.maxY >= cursor.y - apertureWorld
  );
}

/**
 * EXACT nearest distance from `p` to a primitive shape's FINITE (bounded) drawn
 * geometry: a SEG → distance to the finite segment (delegates to the drawing-model
 * one-truth `distanceToSegment2D`); a CIRCLE → radial gap `|dist(p,c) − r|`; an
 * ARC → the radial gap when `p`'s bearing falls WITHIN the sweep, else the nearer
 * endpoint (AutoCAD picks the DRAWN arc, not its full-circle extension).
 *
 * Distinct from `shapeWithinAperture` (a conservative AABB prefilter). The
 * Extended-Intersection override needs the EXACT test: the aperture-box AABB of a
 * long DIAGONAL segment can straddle the cursor while the segment itself is far,
 * so accepting an operand on AABB overlap alone would snap to a distant extended
 * crossing for an object NOT actually under the pickbox (@codex #134). The second
 * operand MUST be geometrically under the pickbox — this is that gate.
 */
export function shapeDistanceToPoint(s: IntersectShape, p: Vec2): number {
  if (s.kind === "seg") return distanceToSegment2D(p, s.a, s.b);
  if (s.kind === "circle") {
    return Math.abs(Math.hypot(p.x - s.c.x, p.y - s.c.y) - s.r);
  }
  // arc — finite sweep: in-sweep bearing → radial gap to the ring; otherwise the
  // nearer of the two arc endpoints.
  if (pointOnArc(p, s)) {
    return Math.abs(Math.hypot(p.x - s.c.x, p.y - s.c.y) - s.r);
  }
  const e1 = arcPointAtAngle(s, s.start);
  const e2 = arcPointAtAngle(s, s.end);
  return Math.min(
    Math.hypot(p.x - e1.x, p.y - e1.y),
    Math.hypot(p.x - e2.x, p.y - e2.y),
  );
}

/** Result of a streaming shape gather: the owned shapes + which caps tripped. */
export interface CollectedShapes {
  shapes: OwnedShape[];
  /** APPINT-only: the aperture entity set exceeded `INTERSECT_PASS_ENTITY_CAP`. */
  entityCapped: boolean;
  /** The total near-cursor / round-robin shape budget was hit. */
  shapeCapped: boolean;
  /**
   * BOUNDED-only: the total-segments-SCANNED backstop (`INTERSECT_PASS_SCAN_CAP`)
   * was hit before the shape budget — i.e. the gather examined a pathological
   * number of FAR segments that filtered to nothing. Distinct from `shapeCapped`
   * (which bounds RETAINED shapes); this bounds WORK so a zero-collected scan can
   * never run unbounded.
   */
  scanCapped: boolean;
}

/**
 * BOUNDED-INTERSECTION streaming gather (@codex round-4 #651/#620/#670 +
 * round-4-followup #651b/#651c).
 *
 * Walk EVERY aperture entity and STREAM its edges/arcs via
 * `streamShapesForEntity`, keeping ONLY the shapes whose finite AABB overlaps
 * the cursor-aperture box. A near-cursor crossing lies ON both shapes, so a
 * shape whose AABB misses the box can produce no in-aperture bounded crossing
 * and is dropped WITHOUT being retained — a 100k-segment polyline materializes
 * just the handful of edges straddling the cursor (finding #651:
 * generate-and-filter per shape, the full array is never built).
 *
 * There is NO entity pre-slice (finding #620): we iterate ALL aperture entities
 * and an entity contributing ZERO near-cursor shapes consumes NO RETAINED budget,
 * so the two ACTUAL crossing entities are reached even when hundreds of
 * large-bbox (but far-segment) polylines/blocks precede them.
 *
 * Two backstops make the WORK — not just the retained shapes — bounded, fixing
 * the round-4 regressions:
 *
 *   • SCAN budget (round-4-followup #651b): the retained-shape cap alone does
 *     NOT bound a gather that collects nothing. When many large-AABB-but-FAR
 *     entities overlap the aperture (the spatial index returns them) yet none of
 *     their segments are near-cursor, a naive scan streams EVERY segment of EVERY
 *     entity, retains zero, and the retained cap never trips (e.g. 200 polylines
 *     × 5000 far segments = 1M segments scanned per hover). We count every
 *     segment/arc EXAMINED and break once `INTERSECT_PASS_SCAN_CAP` is hit, so
 *     the bound caps WORK regardless of how few shapes are retained.
 *
 *   • ROUND-ROBIN + per-owner cap (round-4-followup #651c): draining each
 *     entity's near-cursor shapes sequentially lets a single dense entity (≥
 *     SHAPE_CAP near-cursor edges crammed in the aperture) consume the ENTIRE
 *     total-shape budget — so a line crossing one of its early edges collects
 *     ZERO shapes and (same-owner pairs skipped) its crossing is LOST. We fill
 *     the total budget ROUND-ROBIN across owners (one near-cursor shape per owner
 *     per round) — the same fairness machinery the APPINT path uses — so a
 *     crossing's two distinct owners are both represented. A DYNAMIC per-owner
 *     fair share (`ceil(SHAPE_CAP / ownerCount)`, round-5 #742) additionally
 *     caps any one owner's contribution ONLY under global pressure (with one
 *     owner the share is the whole SHAPE_CAP, so a dense single polyline keeps
 *     all its near-cursor segments); when it truncates an owner it retires ONLY
 *     that owner (the round-robin continues for the others) and flags the gather
 *     capped so the over-cap warning fires (round-5 #347: this truncation must
 *     NOT terminate the whole loop, else still-budgeted owners get starved).
 *
 * The total bound is `INTERSECT_PASS_SHAPE_CAP` retained shapes; the streaming
 * aperture filter makes hitting it pathological rather than the primary bound.
 */
export function collectBoundedNearCursorShapes(
  entities: readonly RenderableEntity[],
  cursor: Vec2,
  apertureWorld: number,
): CollectedShapes {
  // Per-owner lazy generator-iterator over its straight/conic shapes. `next()`
  // pulls one shape at a time, so a 100k-segment polyline is walked only as deep
  // as the round-robin + aperture filter demand — never fully materialized.
  const cursors: Array<{
    ownerId: string;
    it: Iterator<IntersectShape>;
    /** near-cursor shapes already taken from this owner (per-owner ceiling). */
    taken: number;
  }> = [];
  // Count CONTRIBUTING owners — those whose entity kind can yield ≥1 straight/
  // conic shape (@codex round-6 #2). The fair-share denominator must EXCLUDE
  // non-contributing owners (point / text / block-insert / ellipse / spline /
  // compound stream ZERO shapes): basing it on the raw candidate count let a
  // dense aperture of such owners make `ceil(SHAPE_CAP / ownerCount)` tiny and
  // STARVE a legit dense polyline even though the global budget was mostly free.
  // Pre-filtering by entity kind here (option (b)) keeps it simple + correct and
  // does NOT alter the round-robin loop (so it cannot re-introduce the #347
  // loop-termination bug — the loop still only ends on the GLOBAL caps / active==0).
  let contributingOwnerCount = 0;
  for (const e of entities) {
    if (!e) continue;
    cursors.push({ ownerId: e.id, it: streamShapesForEntity(e), taken: 0 });
    if (entityCanProduceIntersectShape(e)) contributingOwnerCount++;
  }
  const shapes: OwnedShape[] = [];
  let shapeCapped = false;
  let scanCapped = false;
  // Set when an owner is retired at its per-owner fair share. Kept SEPARATE from
  // the loop-terminating `shapeCapped` so retiring one saturated owner does NOT
  // abort the round-robin for the OTHER still-budgeted owners (round-5 #347:
  // overloading `shapeCapped` here re-created the 'crossing LOST' bug — the first
  // owner to saturate starved every other owner even with global budget free).
  // OR'd into the returned `shapeCapped` so the over-cap warning still fires when
  // any per-owner truncation occurs.
  let ownerTruncated = false;
  let scanned = 0;
  // DYNAMIC per-owner fair share of the GLOBAL shape budget (round-5 #742). With
  // ONE owner this equals the whole `INTERSECT_PASS_SHAPE_CAP`, so a single dense
  // polyline under the cursor (global budget free) keeps ALL its near-cursor
  // segments — the share only bites when MANY owners contend. Truncating an owner
  // at this share flags `ownerTruncated` (OR'd into the returned `shapeCapped` →
  // warns once) but does NOT abort the loop for the other owners.
  const perOwnerFairShare = intersectPassPerOwnerFairShare(
    contributingOwnerCount,
  );
  // `done[i]` marks an owner whose generator is exhausted (skipped thereafter).
  const done = new Array<boolean>(cursors.length).fill(false);
  let active = cursors.length;
  // Round-robin: each round, EXAMINE exactly ONE shape from every still-active
  // owner. Examining one shape per owner per round (not draining a whole owner
  // before the next) distributes BOTH the retained-shape budget AND the scan
  // work fairly — so a far-only entity with a million segments cannot starve a
  // later crossing line of its turn before the scan backstop fires, and a dense
  // near-cursor entity cannot monopolise the retained budget. Each examined
  // shape (near OR far) counts toward the SCAN backstop, so a zero-collected
  // scan is bounded by `INTERSECT_PASS_SCAN_CAP` examinations. Stop when the
  // total-shape budget is hit, the scan backstop is hit, or every owner is
  // exhausted.
  while (active > 0 && !shapeCapped && !scanCapped) {
    for (let i = 0; i < cursors.length; i++) {
      if (done[i]) continue;
      const c = cursors[i]!;
      if (c.taken >= perOwnerFairShare) {
        // Owner reached its DYNAMIC fair share of the global budget — retire ONLY
        // this owner from the rotation so the remaining budget goes to the OTHER
        // owners (the loop ends naturally once `active==0`, or via the genuine
        // global SHAPE_CAP / SCAN_CAP breaks). This bites ONLY under global
        // pressure (with one owner the share equals SHAPE_CAP, so nothing is
        // dropped while budget remains). Dropping a near-cursor shape here IS a
        // truncation, so mark `ownerTruncated` (OR'd into the returned
        // `shapeCapped` → warns once) — round-5 #742: the prior FIXED ceiling
        // retired silently; round-5 #347: setting the loop-terminating
        // `shapeCapped` here aborted the gather for every other still-budgeted
        // owner, re-creating the 'crossing LOST' bug.
        ownerTruncated = true;
        done[i] = true;
        active--;
        continue;
      }
      if (scanned >= INTERSECT_PASS_SCAN_CAP) {
        scanCapped = true;
        break;
      }
      const step = c.it.next();
      if (step.done) {
        done[i] = true;
        active--;
        continue;
      }
      scanned++;
      if (!shapeWithinAperture(step.value, cursor, apertureWorld)) continue;
      if (shapes.length >= INTERSECT_PASS_SHAPE_CAP) {
        shapeCapped = true;
        break;
      }
      shapes.push({ shape: step.value, ownerId: c.ownerId });
      c.taken++;
    }
  }
  return {
    shapes,
    entityCapped: false,
    shapeCapped: shapeCapped || ownerTruncated,
    scanCapped,
  };
}

/**
 * APPINT streaming gather (round-robin, multi-owner fair).
 *
 * An apparent crossing lies on the INFINITE extension, arbitrarily far from the
 * finite AABB, so the per-shape aperture prefilter CANNOT apply here. The
 * entity set is instead bounded by `INTERSECT_PASS_ENTITY_CAP` (the resolver's
 * entity-broaden window already pre-bounds it; this is the defensive backstop),
 * and the total-shape budget is filled ROUND-ROBIN so a single huge polyline
 * does NOT consume the whole budget and starve later owners — a line crossing
 * one of the polyline's early edges still gets a slot (same-owner pairs are
 * skipped, so the line MUST be represented or its crossing is lost).
 *
 * To stay streaming we lazily pull one shape per owner per round via a
 * per-entity generator iterator (`streamShapesForEntity`), expanding each
 * owner's next shape only when its turn comes — a 100k-segment polyline is
 * walked at most `ceil(SHAPE_CAP / owners)` edges deep, never fully
 * materialized.
 */
function collectAppintShapesRoundRobin(
  entities: readonly RenderableEntity[],
): CollectedShapes {
  let entityCapped = false;
  let list = entities;
  if (entities.length > INTERSECT_PASS_ENTITY_CAP) {
    entityCapped = true;
    list = entities.slice(0, INTERSECT_PASS_ENTITY_CAP);
  }
  // Per-owner lazy generator-iterator over its straight/conic shapes (vertex
  // order). `next()` pulls one shape at a time, so a huge polyline is walked
  // only as deep as the round-robin demands — never fully materialized up front.
  const cursors: Array<{
    ownerId: string;
    it: Iterator<IntersectShape>;
  }> = [];
  for (const e of list) {
    if (!e) continue;
    cursors.push({ ownerId: e.id, it: streamShapesForEntity(e) });
  }
  const shapes: OwnedShape[] = [];
  let shapeCapped = false;
  let active = cursors.length;
  // Round-robin: one shape from each owner per round until every owner is
  // exhausted or the shape budget is hit. Fair share guarantees later owners
  // are represented even when an early owner is huge.
  while (active > 0 && !shapeCapped) {
    active = 0;
    for (const c of cursors) {
      const step = c.it.next();
      if (step.done) continue;
      active++;
      if (shapes.length >= INTERSECT_PASS_SHAPE_CAP) {
        shapeCapped = true;
        break;
      }
      shapes.push({ shape: step.value, ownerId: c.ownerId });
    }
  }
  // APPINT has no per-shape aperture prefilter, so there is no "scanned far
  // shapes that filtered to nothing" case — every examined shape is retained
  // until the budget is hit. `scanCapped` is BOUNDED-only.
  return { shapes, entityCapped, shapeCapped, scanCapped: false };
}

/**
 * Test-only seam exposing the streaming gather result so a test can assert how
 * many primitive shapes were actually MATERIALIZED for a resolve (finding #651:
 * a 100k-segment polyline under the cursor must collect only the handful of
 * near-cursor shapes, never the full 100k). NOT used in production — the hot
 * path calls the collectors directly. Returns the owner ids of the collected
 * shapes + the cap flags.
 */
export function __collectShapesForTest(
  entities: readonly RenderableEntity[],
  cursor: Vec2,
  mode: "intersection" | "appint",
  apertureWorld: number,
): {
  ownerIds: string[];
  entityCapped: boolean;
  shapeCapped: boolean;
  scanCapped: boolean;
} {
  const collected =
    mode === "intersection"
      ? collectBoundedNearCursorShapes(entities, cursor, apertureWorld)
      : collectAppintShapesRoundRobin(entities);
  return {
    ownerIds: collected.shapes.map((s) => s.ownerId),
    entityCapped: collected.entityCapped,
    shapeCapped: collected.shapeCapped,
    scanCapped: collected.scanCapped,
  };
}

/**
 * Pairwise INTERSECTION / APPARENT-INTERSECTION pass over the
 * aperture-bounded candidate entities. For every UNORDERED entity pair,
 * decompose both into straight/conic shapes and append a RawHit for each
 * crossing within `apertureWorld` of the cursor.
 *
 * `mode` selects the semantics: `"intersection"` → bounded crossings (on
 * both spans / sweeps); `"appint"` → extended crossings (infinite line /
 * full circle). For TRANSVERSAL crossings `appint ⊇ intersection` follows
 * from `bounded=false` dropping the on-span / on-sweep filter (the
 * bounded-only degenerate shared-endpoint joins are INTERSECTION-only —
 * coincident extensions have no isolated apparent crossing). The
 * `ownerEntityId` recorded is
 * the lexically-smaller of the two so the resolver's existing tie-break
 * stays deterministic and order-independent.
 *
 * Self-pairs ARE included (i !== j only across DISTINCT entities, but an
 * entity is never paired with itself) — a single polyline whose own edges
 * cross is NOT computed here (AutoCAD treats self-intersection of one
 * object via INT only at shared vertices, which Endpoint already covers);
 * cross-entity pairs are the INT/APPINT use case.
 */
export function appendIntersectionCandidates(
  entities: readonly RenderableEntity[],
  cursor: Vec2,
  mode: "intersection" | "appint",
  apertureWorld: number,
  out: RawHit[],
): void {
  const bounded = mode === "intersection";
  // STREAMING COLLECTOR (@codex round-4 #651/#620/#670 — clean redesign). The
  // round-3 machinery materialized every entity's full shape array
  // (`shapesForEntity`) and only THEN filtered/capped, so a 100k-segment
  // polyline allocated 100k objects on EVERY hover before a single one was
  // dropped, and the entity cap pre-sliced the first 200 aperture entities
  // BEFORE the near-cursor filter (so 200 large-bbox-but-far entities could
  // starve the two ACTUAL crossing entities further down the list). Both fall
  // out of one streaming pass:
  //
  //   • BOUNDED INTERSECTION — a crossing lies ON both shapes, so a shape whose
  //     finite AABB misses the cursor-aperture box can produce NO in-aperture
  //     crossing. We STREAM each entity's edges/arcs via `streamShapesForEntity`
  //     and keep ONLY the near-cursor ones (generate-and-filter per shape), so a
  //     100k-segment polyline materializes just the handful straddling the
  //     cursor — the full array is never built. There is NO entity pre-slice:
  //     we walk ALL aperture entities ROUND-ROBIN and an entity contributing
  //     zero near-cursor shapes consumes NO RETAINED budget, so the real
  //     crossing entities are reached regardless of how many far large-bbox
  //     entities precede them. Two backstops bound the WORK (not just the
  //     retained shapes): a total-segments-SCANNED cap so a zero-collected scan
  //     over many far-but-large entities cannot run unbounded, and round-robin +
  //     a DYNAMIC per-owner fair share (only biting under global pressure) so a
  //     single dense entity cannot monopolise the budget and starve a crossing
  //     line while never dropping near-cursor shapes when budget remains free.
  //
  //   • APPINT — an apparent crossing lies on the INFINITE extension,
  //     arbitrarily far from the finite AABB, so the per-shape prefilter CANNOT
  //     apply (a far stub whose extension crosses under the cursor must be
  //     kept). APPINT therefore keeps the ENTITY cap (the entity-broaden window
  //     already bounds the set) AND a round-robin total-shape cap so a single
  //     huge polyline does not starve later owners (a line crossing one of its
  //     early edges still gets a slot — same-owner pairs are skipped, so the
  //     line MUST be represented).
  const collected = bounded
    ? collectBoundedNearCursorShapes(entities, cursor, apertureWorld)
    : collectAppintShapesRoundRobin(entities);
  const shapes = collected.shapes;
  if (collected.entityCapped) {
    warnIntersectCapOnce(
      `[osnap] intersection pass capped at ${INTERSECT_PASS_ENTITY_CAP} of ${entities.length} aperture candidates`,
    );
  }
  if (collected.shapeCapped) {
    warnIntersectCapOnce(
      `[osnap] intersection pass capped at ${INTERSECT_PASS_SHAPE_CAP} primitive shapes (large polyline aperture); some crossings may be omitted`,
    );
  }
  if (collected.scanCapped) {
    warnIntersectCapOnce(
      `[osnap] intersection pass capped at ${INTERSECT_PASS_SCAN_CAP} segments scanned (pathological far-entity density); some crossings may be omitted`,
    );
  }
  // Pairwise sweep over the flat shape list. Skip same-owner pairs (an
  // entity is never intersected with itself — AutoCAD covers a polyline's
  // own shared vertices via Endpoint). ownerEntityId is the lexically
  // smaller of the two owners so the resolver tie-break stays deterministic.
  //
  // EMIT CAP (@codex round-5 #1, tie-break-corrected round-6 #4): the shape
  // gather is bounded, but the O(M²) pairwise sweep can still find an UNBOUNDED
  // number of crossings (a dense aperture of short entities all crossing near one
  // point). Rather than push every crossing into `out`, this pass collects its
  // crossings into a bounded BEST-K buffer (`INTERSECT_PASS_EMIT_CAP`) ordered by
  // the resolver's tie-break (distWorld THEN lexically-smaller ownerEntityId):
  // when the buffer is full and a new crossing is BETTER than the current worst
  // retained, the worst is evicted. The resolver picks the single best candidate,
  // so retaining the K best under the SAME ordering GUARANTEES the candidate the
  // uncapped resolver would pick is never dropped by the cap — including at a
  // distance tie where the smaller ownerEntityId wins (a distance-only eviction
  // would have dropped it). The retained buffer is flushed into `out` after the
  // sweep; truncation shares the rate-limited warn.
  const kBuf: RawHit[] = [];
  // Index of the current WORST retained hit (the eviction target). "Worst" is
  // the candidate the uncapped resolver would pick LAST under its documented
  // tie-break (@codex round-6 #4): distance THEN lexicographically-smaller
  // ownerEntityId. So the worst-retained is the one with the LARGEST distWorld,
  // and among equal-largest distances the one with the LARGEST (lexically-later)
  // ownerEntityId. Tracking the worst by this FULL ordering — not distance alone
  // — guarantees the retained-K is exactly the set the resolver would prefer, so
  // a later EQUAL-distance crossing with a smaller ownerEntityId correctly evicts
  // the larger-ownerEntityId tie (the prior strictly-smaller-distance eviction
  // dropped it, diverging from the uncapped resolver at a tie).
  let worstIdx = -1;
  // `worseThan(a, b)` — true iff hit `a` is WORSE than hit `b` under the resolver
  // tie-break (farther, or equal-distance with a lexically-LARGER ownerEntityId).
  const worseThan = (a: RawHit, b: RawHit): boolean => {
    if (a.distWorld > b.distWorld + 1e-12) return true;
    if (a.distWorld < b.distWorld - 1e-12) return false;
    // Equal distance within EPS → the larger ownerEntityId is the worse (the
    // resolver prefers the lexically-SMALLER id on a tie).
    return a.ownerEntityId > b.ownerEntityId;
  };
  let emitCapped = false;
  const recomputeWorst = (): void => {
    worstIdx = -1;
    for (let k = 0; k < kBuf.length; k++) {
      if (worstIdx === -1 || worseThan(kBuf[k]!, kBuf[worstIdx]!)) {
        worstIdx = k;
      }
    }
  };
  for (let i = 0; i < shapes.length; i++) {
    const a = shapes[i]!;
    for (let j = i + 1; j < shapes.length; j++) {
      const b = shapes[j]!;
      if (a.ownerId === b.ownerId) continue;
      const ownerId = a.ownerId < b.ownerId ? a.ownerId : b.ownerId;
      for (const p of intersectShapes(a.shape, b.shape, bounded)) {
        const dist = Math.hypot(p.x - cursor.x, p.y - cursor.y);
        if (dist > apertureWorld) continue;
        const hit: RawHit = {
          mode: mode as OsnapMode,
          world: p,
          ownerEntityId: ownerId,
          distWorld: dist,
        };
        if (kBuf.length < INTERSECT_PASS_EMIT_CAP) {
          kBuf.push(hit);
          if (worstIdx === -1 || worseThan(hit, kBuf[worstIdx]!)) {
            worstIdx = kBuf.length - 1;
          }
          continue;
        }
        // Buffer full — evict the current WORST retained ONLY if this crossing is
        // BETTER than it under the resolver tie-break (strictly closer, OR equal
        // distance with a smaller ownerEntityId). Matching the resolver ordering
        // here — not distance alone — guarantees the retained-K always contains
        // the candidate the uncapped resolver would pick, including at a distance
        // tie broken by ownerEntityId (@codex round-6 #4).
        emitCapped = true;
        if (worseThan(kBuf[worstIdx]!, hit)) {
          kBuf[worstIdx] = hit;
          recomputeWorst();
        }
      }
    }
  }
  if (emitCapped) {
    warnIntersectCapOnce(
      `[osnap] intersection pass capped at ${INTERSECT_PASS_EMIT_CAP} emitted crossings (dense aperture); keeping the ${INTERSECT_PASS_EMIT_CAP} closest`,
    );
  }
  for (const hit of kBuf) out.push(hit);
}
